import { useEffect, useRef } from "react";

/** Visible by default; only "arms" the hidden state once JS has set up the observer.
 *  This guarantees the content is never stuck invisible if observers fail. */
export default function Reveal({ children, delay = 0, as: As = "div", className = "", ...rest }) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (!("IntersectionObserver" in window)) {
      el.classList.add("is-visible");
      return;
    }

    // Arm the hidden state, then observe.
    el.dataset.fsArmed = "true";

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setTimeout(() => el.classList.add("is-visible"), delay);
            io.unobserve(el);
          }
        });
      },
      { threshold: 0.08, rootMargin: "0px 0px -40px 0px" }
    );
    io.observe(el);

    // Failsafe: if for any reason the observer doesn't fire within 1.5s, force-show.
    const failsafe = setTimeout(() => {
      el.classList.add("is-visible");
    }, 1500);

    return () => {
      io.disconnect();
      clearTimeout(failsafe);
    };
  }, [delay]);

  return (
    <As ref={ref} className={`fs-reveal ${className}`} {...rest}>
      {children}
    </As>
  );
}
